<template>
  <section class="lexbox-redirect-container">
    <HeaderComponent />
    <section class="lexbox-redirect-section">
      <PromptDialogComponent
        :width="753"
        :title="texts.dialogTitle"
        :subTitle="texts.dialogSubTitle"
        :isOpened="showDialog"
        :formSchema="nifSchema"
        context="sso-payments"
        :formFieldValues="formFieldValues"
        @execute-action="executeDialogAction"
        :texts="texts.buttons"
      />
    </section>
  </section>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n-composable'
import store from '@/store/store'
import HeaderComponent from '@/components/Header/HeaderComponent.vue'
import PromptDialogComponent from '@/components/Dialog/PromptDialogComponent.vue'
import { MainService } from '@/services/MainService'
import { URLS } from '@/router/routes/urlRoutes'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { DialogTypes } from '@/store/modules/dialog/dialogTypes'

interface FormFieldValues {
  nif: string
}

const showDialog = ref(false)
const formFieldValues = ref<FormFieldValues>({ nif: '' })
const { t } = useI18n()
const texts = {
  dialogTitle: t('components.dialog.sso_paymens.title') as string,
  dialogSubTitle: t('components.dialog.sso_paymens.sub_title') as string,
  nifLabel: t('components.dialog.sso_paymens.dni_label') as string,
  buttons: {
    closeButton: t('action_buttons.cancel') as string,
    saveButton: t('action_buttons.save') as string
  }
}

const nifSchema = [
  {
    id: 'nif',
    name: 'nif',
    type: 'nif',
    label: texts.nifLabel,
    fieldType: 'LexonTextFieldComponent',
    validationRules: 'required|max:200',
    xs: 12,
    sm: 12,
    md: 12,
    lg: 12,
    xl: 12,
    'offset-xs': 0,
    'offset-sm': 0,
    'offset-md': 0,
    'offset-lg': 0,
    'offset-xl': 0,
    props: { maxlength: '15' }
  }
]

const nif = computed(() => store.getters[`${ModuleNamespaces.AUTH}/getUserNif`])
const accessToken = computed(() => store.getters[`${ModuleNamespaces.AUTH}/getAuthToken`])

async function executeDialogAction(actionName: string) {
  switch (actionName) {
    case 'close':
      showDialog.value = false
      window.close()
      break
    case 'save':
      await saveNif()
      break
  }
}

async function saveNif() {
  const nifValue = formFieldValues.value.nif
  const { code } = await store.dispatch(`${ModuleNamespaces.AUTH}/saveUserNif`, nifValue)
  await store.dispatch(`${ModuleNamespaces.AUTH}/refreshToken`)
  if (code === 0) {
    redirectToService()
  } else {
    window.location.replace(`/${URLS.DASHBOARD}`)
  }
}

async function redirectToService() {
  try {
    const url = `/sso-payments/url-login/get`
    const { data } = await new MainService().refreshAccessToken(accessToken.value).getData(url)
    window.location.replace(data)
  } catch (e) {
    store.dispatch(`${ModuleNamespaces.DIALOG}/showDialog`, {
      message: t('components.dialog.sso_paymens.error'),
      type: DialogTypes.ERROR,
      action: async () => window.close()
    })
  } finally {
    store.commit(`${ModuleNamespaces.SPINNER}/SHOW_SPINNER`, false)
  }
}

onMounted(() => {
  if (!nif.value) {
    showDialog.value = true
  } else {
    store.commit(`${ModuleNamespaces.SPINNER}/SHOW_SPINNER`, true)
    redirectToService()
  }
})
</script>

<style lang="scss" scoped>
.lexbox-redirect {
  &-container {
    @include external-view-layout;
  }

  &-section {
    @include flex;
    flex-direction: column;
    margin: 30px;
  }

  &-card {
    @include flex($flex-direction: column, $justify-content: flex-start);
    @include borders;
    width: 450px;
    padding-top: 50px;
  }
}
</style>
